import { createContext, useContext, useState, useEffect } from "react";

const translations = {
    en: {
        nav: { home: "Home", devices: "Devices", about: "About", contact: "Contact", admin: "Admin" },
        auth: {
            signIn: "Sign in", signUp: "Sign up", logout: "Log out", account: "Account", dashboard: "Dashboard",
            signInTitle: "Welcome back.", signUpTitle: "Create your account.",
            signInSub: "Sign in to your KORHC account.", signUpSub: "Join KORHC to track orders and inquiries.",
            name: "Name", username: "Username", email: "Email", password: "Password",
            phName: "Jane Doe", phUsername: "janedoe", phEmail: "you@example.com",
            toggleToSignUp: "Don't have an account? Sign up", toggleToSignIn: "Already have an account? Sign in",
            creating: "Creating…", signingIn: "Signing in…",
            successIn: "Welcome back.", successUp: "Account created — welcome!",
            google: "Continue with Google", or: "or",
        },
        admin: {
            products: "Products", inquiries: "Inquiries", categories: "Categories", signOut: "Sign out",
            statProducts: "Products", statTotal: "Total inquiries", statNew: "New inquiries",
            devices: "Devices", addDevice: "Add device",
            thDevice: "Device", thCategory: "Category", thPrice: "Price", thFeatured: "Featured", thActions: "Actions",
            yes: "Yes", no: "No",
            noInquiries: "No inquiries yet.", interestedIn: "Interested in:",
            stNew: "new", stRead: "read", stResponded: "responded",
            newCategoryPh: "New category name", add: "Add", noCategories: "No categories yet.",
            device: "device", devicesCount: "devices",
            addDeviceTitle: "Add device", editDevice: "Edit device",
            fName: "Name", fCategory: "Category", fTagline: "Tagline", fPrice: "Price", fOrder: "Order",
            fFeatured: "Featured", fImage: "Main image URL", fShort: "Short description", fDesc: "Full description",
            fFeatures: "Features (one per line)", fBenefits: "Benefits (one per line)",
            fSpecs: "Specs (Label: Value — one per line)", fGallery: "Gallery image URLs (one per line)",
            cancel: "Cancel", saveDevice: "Save device", saving: "Saving…",
            tNameReq: "Name is required.", tProductSaved: "Product saved.", tDeleted: "Deleted.",
            tCatAdded: "Category added.", tCatUpdated: "Category updated.", tCatDeleted: "Category deleted.",
            confirmDeleteDevice: "Delete this device?", confirmDeleteCat: "Delete this category?",
        },
        cta: {
            requestDemo: "Request a demo", explore: "Explore devices", howItWorks: "How it works",
            viewAll: "View all devices", talkToTeam: "Talk to our team", bookDemo: "Book a demo",
            requestPricing: "Request pricing & demo", backToDevices: "All devices",
            sendInquiry: "Send inquiry", sending: "Sending…",
        },
        hero: {
            eyebrow: "Electrotherapy, engineered",
            lines: ["ELECTRIC", "RELIEF, NOW", "IN YOUR HAND."],
            sub: "KORHC builds clinically-cleared TENS & EMS devices that stop pain and rebuild muscle — without a single pill.",
            flagship: "Flagship",
        },
        stats: { freq: "Frequency range", cleared: "Cleared devices", battery: "Battery per charge", clinical: "Clinical stimulation" },
        manifesto: {
            eyebrow: "The manifesto",
            chapters: [
                { t: "Interrupt the signal", d: "Our devices deliver precisely-calibrated pulses that intercept pain signals before they reach the brain — clinically documented, drug-free." },
                { t: "Re-educate the muscle", d: "EMS protocols recruit dormant fibres, rebuilding strength and circulation after injury or surgery without strain." },
                { t: "Recover on your terms", d: "From a pocket unit to a four-channel clinic console, every KORHC speaks the same language: relief, repeatable." },
            ],
        },
        featured: { eyebrow: "The range", title: "Four devices.\nOne current of relief." },
        footer: {
            tagline: "Drug-free relief, engineered", feel: "Feel the current.\nNot the pain.",
            explore: "Explore", certified: "Certified",
            rights: "COPYRIGHT© 2017 Korea Health & Beauty ALL RIGHT RESERVED",
        },
        products: {
            eyebrow: "The catalogue", title: "Every device we make.",
            sub: "From pocket TENS units to four-channel clinic consoles — filter the range by category.",
            all: "All", empty: "No devices in this category yet.",
        },
        detail: {
            backToDevices: "All devices", incl: "incl. electrodes & charger",
            capabilities: "Capabilities", specifications: "Specifications",
            notFound: "Device not found", loading: "Loading…",
        },
        about: {
            eyebrow: "Our reason", title: "We believe relief shouldn't come in a bottle.",
            intro: "Founded in 2005, Korea Health & Beauty is a global healthcare company dedicated to enhancing human health through innovative medical solutions. The company researches, manufactures, and exports a wide range of products—from everyday LED healthcare devices to professional medical equipment. By merging alternative medicine with advanced IT and multi-wavelength LED ARRAY technology, featured in its flagship Pulse Line system, Korea Health & Beauty continues to deliver cutting-edge technology and exceptional quality to international markets.",
            howEyebrow: "How it works", howTitle: "Four steps between you and relief.",
            steps: [
                { t: "Electrodes on skin", d: "Self-adhesive electrode pads sit over the target region — no gels, no fuss." },
                { t: "Calibrated pulses fire", d: "The device emits precise low-voltage pulses tuned to your selected therapy program." },
                { t: "Pain gate closes", d: "Pulses flood the nerve pathway, out-competing pain signals before they reach the brain." },
                { t: "Muscle responds", d: "In EMS mode, the same current contracts and re-educates the muscle, restoring strength." },
            ],
            ctaTitle: "Ready to feel the difference?",
            globalEyebrow: "Global presence",
            globalTitle: "Serving markets\nacross the world.",
            globalDesc: "From our roots in South Korea, Korea Health & Beauty now serves partners and patients across Asia — with an established presence in Myanmar, China, South Korea, Indonesia and Malaysia, and a growing reach into new international markets.",
            countries: { myanmar: "Myanmar", china: "China", korea: "South Korea", indonesia: "Indonesia", malaysia: "Malaysia" },
        },
        contact: {
            eyebrow: "Get in touch", title: "Let's talk relief.",
            sub: "Whether you're a clinic, distributor, or individual — tell us what you need and we'll match you with the right device.",
            emailL: "Email", phoneL: "Phone", hqL: "HQ",
            fName: "Name", fEmail: "Email", fPhone: "Phone", fCompany: "Company",
            fInterested: "Interested in", fSelect: "Select a device (optional)", fMessage: "Message",
            phName: "Jane Doe", phEmail: "jane@clinic.com", phPhone: "+1 555 000 0000",
            phCompany: "Clinic / Distributor", phMessage: "Tell us what you're looking for…",
            tFill: "Please fill in your name, email and message.",
            tSuccess: "Inquiry sent — our team will be in touch shortly.",
            tError: "Could not send inquiry.",
        },
    },
    ko: {
        nav: { home: "홈", devices: "제품", about: "소개", contact: "문의", admin: "관리자" },
        auth: {
            signIn: "로그인", signUp: "회원가입", logout: "로그아웃", account: "계정", dashboard: "대시보드",
            signInTitle: "다시 오신 것을 환영합니다.", signUpTitle: "계정을 만드세요.",
            signInSub: "KORHC 계정으로 로그인하세요.", signUpSub: "KORHC에 가입하고 주문과 문의를 관리하세요.",
            name: "이름", username: "사용자 이름", email: "이메일", password: "비밀번호",
            phName: "홍길동", phUsername: "honggildong", phEmail: "you@example.com",
            toggleToSignUp: "계정이 없으신가요? 회원가입", toggleToSignIn: "이미 계정이 있으신가요? 로그인",
            creating: "생성 중…", signingIn: "로그인 중…",
            successIn: "다시 오신 것을 환영합니다.", successUp: "계정이 생성되었습니다 — 환영합니다!",
            google: "Google로 계속하기", or: "또는",
        },
        admin: {
            products: "제품", inquiries: "문의", categories: "카테고리", signOut: "로그아웃",
            statProducts: "제품", statTotal: "전체 문의", statNew: "새 문의",
            devices: "제품", addDevice: "제품 추가",
            thDevice: "제품", thCategory: "카테고리", thPrice: "가격", thFeatured: "추천", thActions: "관리",
            yes: "예", no: "아니오",
            noInquiries: "아직 문의가 없습니다.", interestedIn: "관심 제품:",
            stNew: "신규", stRead: "읽음", stResponded: "응답함",
            newCategoryPh: "새 카테고리 이름", add: "추가", noCategories: "아직 카테고리가 없습니다.",
            device: "개 제품", devicesCount: "개 제품",
            addDeviceTitle: "제품 추가", editDevice: "제품 편집",
            fName: "이름", fCategory: "카테고리", fTagline: "태그라인", fPrice: "가격", fOrder: "순서",
            fFeatured: "추천", fImage: "대표 이미지 URL", fShort: "짧은 설명", fDesc: "전체 설명",
            fFeatures: "기능 (한 줄에 하나씩)", fBenefits: "장점 (한 줄에 하나씩)",
            fSpecs: "사양 (레이블: 값 — 한 줄에 하나씩)", fGallery: "갤러리 이미지 URL (한 줄에 하나씩)",
            cancel: "취소", saveDevice: "제품 저장", saving: "저장 중…",
            tNameReq: "이름을 입력해 주세요.", tProductSaved: "제품이 저장되었습니다.", tDeleted: "삭제되었습니다.",
            tCatAdded: "카테고리가 추가되었습니다.", tCatUpdated: "카테고리가 수정되었습니다.", tCatDeleted: "카테고리가 삭제되었습니다.",
            confirmDeleteDevice: "이 제품을 삭제할까요?", confirmDeleteCat: "이 카테고리를 삭제할까요?",
        },
        cta: {
            requestDemo: "데모 요청", explore: "제품 둘러보기", howItWorks: "작동 원리",
            viewAll: "모든 제품 보기", talkToTeam: "팀과 상담하기", bookDemo: "데모 예약",
            requestPricing: "가격 및 데모 요청", backToDevices: "모든 제품",
            sendInquiry: "문의 보내기", sending: "전송 중…",
        },
        hero: {
            eyebrow: "전기치료, 그 이상의 설계",
            lines: ["전기 자극,", "지금 여기", "손안에."],
            sub: "KORHC는 약 없이 통증을 멈추고 근육을 회복시키는 임상 승인 TENS·EMS 기기를 만듭니다.",
            flagship: "대표 제품",
        },
        stats: { freq: "주파수 범위", cleared: "인증 기기", battery: "충전당 사용시간", clinical: "임상 자극 채널" },
        manifesto: {
            eyebrow: "우리의 철학",
            chapters: [
                { t: "신호를 차단하다", d: "정밀하게 조정된 펄스가 통증 신호가 뇌에 도달하기 전에 차단합니다 — 임상적으로 검증된, 약물 없는 방식." },
                { t: "근육을 재교육하다", d: "EMS 프로토콜이 잠든 근섬유를 활성화하여 부상이나 수술 후 무리 없이 근력과 순환을 회복시킵니다." },
                { t: "당신의 방식대로 회복하다", d: "휴대용 기기부터 4채널 클리닉 콘솔까지, 모든 KORHC는 같은 언어를 말합니다: 반복 가능한 완화." },
            ],
        },
        featured: { eyebrow: "제품 라인업", title: "네 가지 기기.\n하나의 완화 흐름." },
        footer: {
            tagline: "약 없는 완화, 정밀한 설계", feel: "전류를 느끼세요.\n통증이 아니라.",
            explore: "둘러보기", certified: "인증",
            rights: "COPYRIGHT© 2017 Korea Health & Beauty ALL RIGHT RESERVED",
        },
        products: {
            eyebrow: "제품 카탈로그", title: "우리가 만드는 모든 기기.",
            sub: "휴대용 TENS 기기부터 4채널 클리닉 콘솔까지 — 카테고리별로 살펴보세요.",
            all: "전체", empty: "이 카테고리에는 아직 기기가 없습니다.",
        },
        detail: {
            backToDevices: "모든 제품", incl: "전극·충전기 포함",
            capabilities: "기능", specifications: "사양",
            notFound: "기기를 찾을 수 없습니다", loading: "불러오는 중…",
        },
        about: {
            eyebrow: "우리의 이유", title: "완화가 약병에서 오지 않아야 한다고 믿습니다.",
            intro: "2005년에 설립된 Korea Health & Beauty는 혁신적인 의료 솔루션을 통해 인류의 건강 증진에 힘쓰는 글로벌 헬스케어 기업입니다. 일상용 LED 헬스케어 기기부터 전문 의료 장비까지 다양한 제품을 연구·제조·수출하고 있습니다. 대체의학과 첨단 IT, 그리고 대표 제품인 Pulse Line 시스템에 적용된 다파장 LED ARRAY 기술을 융합하여, 세계 시장에 첨단 기술과 뛰어난 품질을 지속적으로 제공하고 있습니다.",
            howEyebrow: "작동 원리", howTitle: "당신과 완화 사이의 네 단계.",
            steps: [
                { t: "피부에 전극 부착", d: "자가 접착 전극 패드를 목표 부위에 부착합니다 — 젤도, 번거로움도 없이." },
                { t: "정밀 펄스 방출", d: "선택한 치료 프로그램에 맞춰 정밀한 저전압 펄스를 방출합니다." },
                { t: "통증 관문 차단", d: "펄스가 신경 경로를 채워 통증 신호가 뇌에 도달하기 전에 앞섭니다." },
                { t: "근육 반응", d: "EMS 모드에서는 같은 전류가 근육을 수축·재교육하여 근력을 회복시킵니다." },
            ],
            ctaTitle: "차이를 느낄 준비가 되셨나요?",
            globalEyebrow: "글로벌 네트워크",
            globalTitle: "전 세계 시장에\n함께합니다.",
            globalDesc: "대한민국에서 시작한 Korea Health & Beauty는 이제 아시아 전역의 파트너와 고객에게 서비스를 제공합니다 — 미얀마, 중국, 대한민국, 인도네시아, 말레이시아에 거점을 두고 있으며, 새로운 국제 시장으로 계속 확장하고 있습니다.",
            countries: { myanmar: "미얀마", china: "중국", korea: "대한민국", indonesia: "인도네시아", malaysia: "말레이시아" },
        },
        contact: {
            eyebrow: "문의하기", title: "완화에 대해 이야기해요.",
            sub: "클리닉, 유통업체, 개인 누구든 — 필요한 것을 알려주시면 알맞은 기기를 추천해 드립니다.",
            emailL: "이메일", phoneL: "전화", hqL: "본사",
            fName: "이름", fEmail: "이메일", fPhone: "전화", fCompany: "회사",
            fInterested: "관심 제품", fSelect: "기기 선택 (선택 사항)", fMessage: "메시지",
            phName: "홍길동", phEmail: "hong@clinic.com", phPhone: "+82 10 0000 0000",
            phCompany: "클리닉 / 유통업체", phMessage: "찾고 계신 것을 알려주세요…",
            tFill: "이름, 이메일, 메시지를 입력해 주세요.",
            tSuccess: "문의가 전송되었습니다 — 곧 연락드리겠습니다.",
            tError: "문의를 보내지 못했습니다.",
        },
    },
};

export const LANGUAGES = [
    { code: "en", label: "EN", full: "English" },
    { code: "ko", label: "KO", full: "한국어" },
];

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const [lang, setLang] = useState(() => localStorage.getItem("korhc_lang") || "en");
    useEffect(() => { localStorage.setItem("korhc_lang", lang); }, [lang]);
    const t = translations[lang] || translations.en;
    return (
        <LanguageContext.Provider value={{ lang, setLang, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export const useLang = () => useContext(LanguageContext);
